import { useState, useCallback } from "react";
import { FileText, Upload, CheckCircle2, ExternalLink, X } from "lucide-react";
import { Button } from "./ui/button";
import { toast } from "sonner";

/**
 * ExecutedDocDropZone — Drop zone for uploading signed/executed PDFs.
 * Used in both WO and Proposal forms.
 * 
 * Props:
 *   entityId - WO or Proposal ID
 *   entityType - "wo" | "proposal"
 *   executedDocument - existing executed_document object (if any)
 *   onUpload - async (entityId, formData) => response
 *   onViewUrl - (entityId) => url string
 *   onStatusChange - callback after upload completes (optional)
 */
export function ExecutedDocDropZone({ entityId, entityType, executedDocument, onUpload, onViewUrl, onStatusChange }) {
  const [isDragging, setIsDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadedDoc, setUploadedDoc] = useState(executedDocument);
  const label = entityType === "wo" ? "Work Order" : "Proposal";
  const newStatus = entityType === "wo" ? "Approved" : "Won";

  const handleFile = useCallback(async (file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".pdf")) {
      toast.error("Please upload a PDF file");
      return;
    }
    if (file.size > 25 * 1024 * 1024) {
      toast.error("File too large (max 25MB)");
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await onUpload(entityId, formData);
      setUploadedDoc({ filename: res.data?.filename || file.name, uploaded_at: new Date().toISOString() });
      toast.success(`Executed ${label} uploaded. Status moved to ${newStatus}.`);
      if (onStatusChange) onStatusChange(res.data);
    } catch (err) {
      toast.error(`Upload failed: ${err.response?.data?.detail || err.message}`);
    } finally {
      setUploading(false);
    }
  }, [entityId, onUpload, onStatusChange, label, newStatus]);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    handleFile(file);
  }, [handleFile]);

  const handleFileInput = useCallback((e) => {
    const file = e.target.files[0];
    if (file) handleFile(file);
  }, [handleFile]);

  if (uploadedDoc || executedDocument) {
    const doc = uploadedDoc || executedDocument;
    return (
      <div className="border-2 border-green-200 bg-green-50/50 rounded-lg p-3" data-testid="executed-doc-uploaded">
        <div className="flex items-center gap-2">
          <CheckCircle2 className="w-5 h-5 text-green-600 shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="text-xs font-bold text-green-800">Executed {label}</div>
            <div className="text-[10px] text-green-600 truncate">{doc.filename}</div>
            {doc.uploaded_at && <div className="text-[9px] text-slate-400">Uploaded {new Date(doc.uploaded_at).toLocaleDateString()}</div>}
          </div>
          <a href={onViewUrl(entityId)} target="_blank" rel="noopener noreferrer" className="shrink-0">
            <Button variant="outline" size="sm" className="h-7 text-xs gap-1" data-testid="view-executed-doc">
              <ExternalLink className="w-3 h-3" /> View PDF
            </Button>
          </a>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`border-2 border-dashed rounded-lg p-4 text-center transition-all cursor-pointer ${
        isDragging ? "border-blue-500 bg-blue-50" : "border-slate-300 hover:border-blue-400 hover:bg-blue-50/30"
      } ${uploading ? "opacity-50 pointer-events-none" : ""}`}
      onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
      onDragLeave={() => setIsDragging(false)}
      onDrop={handleDrop}
      onClick={() => document.getElementById(`exec-upload-${entityType}-${entityId}`)?.click()}
      data-testid="executed-doc-dropzone"
    >
      <input
        type="file"
        accept=".pdf"
        className="hidden"
        id={`exec-upload-${entityType}-${entityId}`}
        onChange={handleFileInput}
      />
      {uploading ? (
        <div className="flex flex-col items-center gap-1.5">
          <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
          <span className="text-xs text-blue-600 font-medium">Uploading...</span>
        </div>
      ) : (
        <div className="flex flex-col items-center gap-1.5">
          <Upload className={`w-6 h-6 ${isDragging ? "text-blue-500" : "text-slate-400"}`} />
          <div className="text-xs font-medium text-slate-600">
            Drop signed PDF here
          </div>
          <div className="text-[10px] text-slate-400">
            or click to browse. Auto-moves to <span className="font-semibold">{newStatus}</span>
          </div>
        </div>
      )}
    </div>
  );
}
